import React, { useState, useContext, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';

import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar';
import tiptapExtensions from '../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop';
import { createPost } from '../../api/boardApi';
import { uploadEditorImage } from '../../api/common';
import FileAttachment from '../../components/FileAttachment/FileAttachment';

function WritePost() {
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const pendingImages = useRef(new Map()); // blobUrl → File

  const [searchParams] = useSearchParams();
  const codeId = searchParams.get('code_id');

  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const storeImageLocally = useCallback((file) => {
    const blobUrl = URL.createObjectURL(file);
    pendingImages.current.set(blobUrl, file);
    return blobUrl;
  }, []);

  const editor = useEditorWithDragDrop(tiptapExtensions, '', { onImageUpload: storeImageLocally });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let content = editor.getHTML();

      for (const [blobUrl, file] of pendingImages.current) {
        if (content.includes(blobUrl)) {
          const serverUrl = await uploadEditorImage(file, token, 'board');
          content = content.replaceAll(blobUrl, serverUrl);
        }
        URL.revokeObjectURL(blobUrl);
      }
      pendingImages.current.clear();

      const formData = new FormData();
      formData.append('code_id', codeId);
      formData.append('title', title);
      formData.append('content', content);
      files.forEach(f => formData.append('files', f.file));

      await createPost(formData, token);

      navigate(`/board?code_id=${codeId}`);
    } catch (err) {
      console.error('게시글 등록 실패:', err);
      alert('게시글 등록 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="container-fluid view-post-write">
      <div className="row">
        <div className="col-12">
          <form onSubmit={handleSubmit}>
            {/* 제목 */}
            <div className="mb-3">
              <label className="form-label fw-bold">제목</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* 내용 */}
            <div className="mb-3">
              <label className="form-label fw-bold">내용</label>
              <div className="border p-2 rounded">
                {editor && <EditorToolbar editor={editor} onImageUpload={storeImageLocally} />}
                <EditorContent editor={editor} className="editor-content" />
              </div>
            </div>

            {/* 첨부파일 */}
            <div className="mb-3">
              <label className="form-label fw-bold">첨부파일</label>
              <FileAttachment files={files} setFiles={setFiles} mode="edit" />
            </div>

            {/* 버튼 영역 */}
            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-success">
                작성 완료
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate(`/board?code_id=${codeId}`)}
              >
                목록
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WritePost;
